import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, Users, MessageCircle, Calendar, BarChart3, Ticket } from "lucide-react";
import { api } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  const [params] = useSearchParams();
  const inviteFromUrl = params.get("invite") || "";
  const [tab, setTab] = useState(inviteFromUrl ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [yearsPlayed, setYearsPlayed] = useState("");
  const [inviteCode, setInviteCode] = useState(inviteFromUrl);
  const [inviteValid, setInviteValid] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!inviteCode) { setInviteValid(null); return; }
    let cancel = false;
    api.get(`/invites/validate/${inviteCode}`).then(({ data }) => {
      if (!cancel) setInviteValid(data.valid);
    }).catch(() => { if (!cancel) setInviteValid(false); });
    return () => { cancel = true; };
  }, [inviteCode]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") {
        await login(email, password);
        toast.success("Welcome back!");
      } else {
        if (!/^\d{4}-\d{4}$/.test(yearsPlayed.trim())) {
          toast.error("Years played must look like 2008-2012");
          setBusy(false);
          return;
        }
        await signup({
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          phone,
          years_played: yearsPlayed.trim(),
          invite_code: inviteCode || null,
        });
        toast.success("Account created!");
      }
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-2"
      style={{
        backgroundImage: "linear-gradient(rgba(15,6,32,0.82), rgba(46,16,74,0.86)), url(/umhb-bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12">
        <div className="relative">
          <img src="/cfaa-mark.png" alt="Crusader Football Alumni Association" data-testid="login-logo" className="w-80 h-auto rounded-2xl shadow-2xl" />
          <h1 className="mt-8 text-4xl lg:text-5xl font-black font-display leading-[1.0] tracking-tight text-white">
            Crusader Football<br/>Alumni <span className="text-amber-300">Association</span>
          </h1>
          <p className="mt-6 text-lg text-white/85 max-w-md leading-relaxed">
            Stay connected with your Crusader family — announcements, events, chat, and more, all in one place.
          </p>
        </div>
        <div className="relative grid grid-cols-2 gap-3 max-w-md">
          {[
            { icon: MessageCircle, label: "Live Chat" },
            { icon: Calendar, label: "Events & RSVPs" },
            { icon: BarChart3, label: "Polls" },
            { icon: Users, label: "Members" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 bg-white/15 backdrop-blur rounded-2xl px-4 py-3 text-white">
              <Icon className="w-4 h-4" />
              <span className="text-sm font-semibold">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md bg-white/95 backdrop-blur rounded-3xl p-8 shadow-2xl">
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/cfaa-mark.png" alt="Crusader Football Alumni Association" data-testid="login-logo-mobile" className="w-56 h-auto rounded-2xl shadow-lg" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-black font-display text-slate-900 tracking-tight">
            {tab === "login" ? "Welcome back" : "Join the group"}
          </h2>
          <p className="mt-2 text-slate-500">
            {tab === "login" ? "Pick up where you left off." : "Create your account in seconds."}
          </p>

          <Button
            type="button"
            onClick={handleGoogle}
            data-testid="google-login-btn"
            className="mt-8 w-full h-12 rounded-2xl bg-white border-2 border-slate-200 text-slate-800 hover:bg-slate-50 hover:border-slate-300 font-semibold btn-lift shadow-none"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </Button>

          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Tabs value={tab} onValueChange={setTab} className="mt-6">
            <TabsList data-testid="auth-tabs" className="grid grid-cols-2 w-full rounded-2xl bg-slate-100 p-1 h-12">
              <TabsTrigger value="login" data-testid="auth-tab-login" className="rounded-xl text-slate-700 data-[state=active]:bg-white data-[state=active]:text-purple-800 data-[state=active]:shadow font-bold">Log in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="auth-tab-signup" className="rounded-xl text-slate-700 data-[state=active]:bg-white data-[state=active]:text-purple-800 data-[state=active]:shadow font-bold">Sign up</TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="mt-6 space-y-4">
              {tab === "signup" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="firstName" className="text-slate-700 font-semibold">First name</Label>
                      <Input id="firstName" data-testid="auth-firstname-input" required value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Alex" className="mt-2 h-12 rounded-xl border-slate-200" />
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="text-slate-700 font-semibold">Last name</Label>
                      <Input id="lastName" data-testid="auth-lastname-input" required value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Rivera" className="mt-2 h-12 rounded-xl border-slate-200" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 font-semibold">Phone number</Label>
                    <Input id="phone" data-testid="auth-phone-input" required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" className="mt-2 h-12 rounded-xl border-slate-200" />
                  </div>
                  <div>
                    <Label htmlFor="yearsPlayed" className="text-slate-700 font-semibold">Years played</Label>
                    <Input
                      id="yearsPlayed"
                      data-testid="auth-years-input"
                      required
                      value={yearsPlayed}
                      onChange={(e) => setYearsPlayed(e.target.value)}
                      placeholder="2008-2012"
                      pattern="\d{4}-\d{4}"
                      title="Format: 2008-2012"
                      className="mt-2 h-12 rounded-xl border-slate-200"
                    />
                    <p className="text-xs text-slate-400 mt-1">Format: start year - end year (e.g. 2008-2012)</p>
                  </div>
                </>
              )}
              {tab === "signup" && (
                <div>
                  <Label htmlFor="invite" className="text-slate-700 font-semibold flex items-center gap-1">
                    <Ticket className="w-3.5 h-3.5" /> Invite code <span className="text-xs text-slate-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="invite"
                    data-testid="auth-invite-input"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    placeholder="paste invite code"
                    className={`mt-2 h-12 rounded-xl border-slate-200 ${inviteCode && inviteValid === false ? "border-red-300" : ""} ${inviteCode && inviteValid === true ? "border-emerald-400" : ""}`}
                  />
                  {inviteCode && inviteValid === true && <p className="text-xs text-emerald-600 mt-1 font-semibold">✓ Valid invite</p>}
                  {inviteCode && inviteValid === false && <p className="text-xs text-red-500 mt-1 font-semibold">Invalid code (you can still sign up)</p>}
                </div>
              )}
              <div>
                <Label htmlFor="email" className="text-slate-700 font-semibold">Email</Label>
                <Input id="email" data-testid="auth-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@community.com" className="mt-2 h-12 rounded-xl border-slate-200" />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700 font-semibold">Password</Label>
                <Input id="password" data-testid="auth-password-input" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-2 h-12 rounded-xl border-slate-200" />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="auth-submit-btn"
                className="w-full h-12 rounded-2xl bg-purple-700 hover:bg-purple-800 text-white font-bold text-base btn-lift shadow-md"
              >
                {busy ? "Please wait…" : tab === "login" ? "Log in" : "Create account"}
              </Button>
            </form>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
